import { ServiceResponse } from "@rsm-hcd/javascript-core";
import Button from "atoms/buttons/button";
import ModalCloseButton from "atoms/buttons/modal-close-button";
import { ButtonStyles } from "atoms/constants/button-styles";
import { Icons } from "atoms/constants/icons";
import { LoaderStyles } from "atoms/constants/loader-styles";
import Icon from "atoms/icons/icon";
import Loader from "atoms/loaders/loader";
import { List } from "immutable";
import PublishStatus from "models/enumerations/publish-status";
import EnhancedContentRecord from "models/view-models/enhanced-content-record";
import EnhancedContentResourceRecord from "models/view-models/enhanced-content-resource-record";
import EnhancedContentManifestRecord from "models/view-models/enhanced-content-manifest-record";
import { ModalTransitions } from "molecules/constants/modal-transitions";
import { ModalTypes } from "molecules/constants/modal-types";
import Modal from "molecules/modals/modal";
import NfpaInsightsLabel from "molecules/nfpa-expert-insights-label/nfpa-expert-insights-label";
import PublishStatusBadge from "organisms/admin/situational-navigation/publish-status-badge";
import EnhancedContentDetail, {
    EnhancedContentDetailProps,
} from "organisms/enhanced-content/enhanced-content-detail";
import React, { useEffect, useMemo, useState } from "react";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import { Breakpoints } from "utilities/enumerations/breakpoints";
import useEnhancedContentSearchHit from "utilities/hooks/domain/enhanced-content/use-enhanced-content-search-hit";
import useBreakpoint, {
    BreakpointComparer,
} from "utilities/hooks/use-breakpoint";
import { useLocalization } from "utilities/hooks/use-localization";
import { v4 } from "uuid";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

export interface EnhancedContentPanelProps {
    collapse?: boolean;
    enhancedContent?: EnhancedContentRecord;
    enhancedContentLoading?: boolean;
    enhancedContentManifest?: EnhancedContentManifestRecord;
    getEnhancedContentDetails?: (id: number) => Promise<void>;
    isPreview?: boolean;
    /**
     * Optional callback to be executed when the 'Delete' action modal is confirmed. If not provided,
     * uses the NFPA Insights service for sections to delete.
     */
    onDeleteConfirm?: (
        enhancedContent: EnhancedContentRecord
    ) => Promise<ServiceResponse<Boolean>>;

    /**
     * Optional callback to be executed when the 'Edit' button is clicked. If not provided,
     * uses the event handler from context.
     */
    onEditClick?: () => void;
    onSuccess?: (enhancedContent?: EnhancedContentRecord) => void;
    onToggle?: (isCollapsed: boolean) => void;
    resources?: List<EnhancedContentResourceRecord>;
    resourcesLoading?: boolean;
    resourcesLoaded?: boolean;
    hideAdminTogglesFromAdmins?: boolean;
    isPrintView?: boolean;
    alwaysShow?: boolean;
    observerRef?: (id: number) => (node: HTMLElement | null) => void;
}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

const BASE_CLASS_NAME = "c-enhanced-content-panel";

// #endregion Constants

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const EnhancedContentPanel: React.FC<EnhancedContentPanelProps> = (
    props: EnhancedContentPanelProps
) => {
    const {
        collapse,
        enhancedContent,
        enhancedContentLoading,
        getEnhancedContentDetails,
        enhancedContentManifest,
        isPreview,
        onDeleteConfirm,
        onEditClick,
        onSuccess,
        onToggle,
        resources,
        resourcesLoading,
        hideAdminTogglesFromAdmins = false,
        isPrintView = false,
        alwaysShow = false,
        observerRef,
    } = props;

    // -----------------------------------------------------------------------------------------
    // #region State
    // -----------------------------------------------------------------------------------------
    const { t } = useLocalization();
    const { globalState } = useGlobalState();
    const { currentIdentity } = globalState;
    const hasAdminAccess =
        currentIdentity?.isAdmin() || currentIdentity?.isAuthorOrPublisher();
    const { isCurrentSearchHit } = useEnhancedContentSearchHit();
    const [isCollapsed, setIsCollapsed] = useState<boolean>(
        collapse ?? !isCurrentSearchHit(enhancedContent)
    );
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    // #endregion State

    // -----------------------------------------------------------------------------------------
    // #region Private Properties
    // -----------------------------------------------------------------------------------------

    const accordionContentId = useMemo(() => v4(), []);
    const isMobile = useBreakpoint(
        Breakpoints.Phone,
        BreakpointComparer.MaxWidth
    );
    const collapsedStatusText = isMobile
        ? ""
        : isCollapsed
          ? t("expand")
          : t("collapse");

    const iconType = isMobile
        ? Icons.Enlarge
        : isCollapsed
          ? Icons.Expand
          : Icons.Collapse;

    const enhancedContentDetailProps: EnhancedContentDetailProps = {
        accordionContentId: accordionContentId,
        cssBaseClassName: BASE_CLASS_NAME,
        enhancedContent: enhancedContent!,
        isPreview: isPreview,
        hasAdminAccess: hasAdminAccess,
        onDeleteConfirm: onDeleteConfirm,
        onEditClick: onEditClick,
        onSuccess: onSuccess,
        panelIsCollapsed: isCollapsed,
        resources: resources,
        hideAdminTogglesFromAdmins,
        isPrintView: isPrintView,
    };

    // User settings

    // This is memoized so that content panels are only auto-collapsed when the
    // EnhancedContentExpand setting changes and not whenever any of the global settings change
    const isAutoCollapsed: boolean | undefined = useMemo(() => {
        if (isMobile) {
            return;
        }

        const autoExpand = globalState.getUserSettingValue<boolean>(
            "EnhancedContentExpand"
        );
        if (autoExpand == null) {
            return;
        }

        return !autoExpand;
    }, [globalState, isMobile]);

    const showEnhancedContentUserSetting = globalState.getUserSetting(
        "EnhancedContentShow"
    );

    const expandedCSSModifier =
        !isCollapsed || isPreview || isPrintView ? "-expanded" : "";
    const isLoading = enhancedContentLoading || resourcesLoading;
    // #endregion Private Properties

    // -----------------------------------------------------------------------------------------
    // #region Handlers
    // -----------------------------------------------------------------------------------------

    const loadEnhancedContentDetails = () => {
        if (
            enhancedContent ||
            enhancedContentLoading ||
            enhancedContentManifest?.id == null
        ) {
            return;
        }

        getEnhancedContentDetails?.(enhancedContentManifest.id);
    };

    const handleToggle = () => {
        if (isMobile) {
            const isOpening = !isModalOpen;
            setIsModalOpen(isOpening);
            onToggle?.(!isOpening);
            if (isOpening) {
                loadEnhancedContentDetails();
            }
            return;
        }

        const isOpening = isCollapsed;
        setIsCollapsed(!isCollapsed);
        onToggle?.(!isCollapsed);
        if (isOpening) {
            loadEnhancedContentDetails();
        }
    };

    /**
     * Toggle panel without also firing of the parent onClick event when clicking the button
     * @param e
     */
    const handleToggleButtonClick = (
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        e.stopPropagation();
        handleToggle();
    };

    // #endregion Handlers

    // -----------------------------------------------------------------------------------------
    // #region Side Effects
    // -----------------------------------------------------------------------------------------

    useEffect(
        function initializePanelState() {
            if (collapse == null || isMobile) {
                return;
            }

            setIsCollapsed(collapse);
        },
        [collapse, isMobile]
    );

    useEffect(() => {
        if (isAutoCollapsed == null) return;

        setIsCollapsed(isAutoCollapsed);
    }, [isAutoCollapsed]);

    // #endregion Side Effects

    if (
        !showEnhancedContentUserSetting?.getValue<boolean>() &&
        !isCurrentSearchHit(enhancedContent) && // Ignore user settings when navigating from a search result
        !alwaysShow // Overrides if using somewhere where no usersetting should impact, like Notebooks
    ) {
        return null;
    }

    const enhancedContentHeaderTitleAndPublishInfo = (
        <React.Fragment>
            <NfpaInsightsLabel />
            {hasAdminAccess &&
                !hideAdminTogglesFromAdmins &&
                !isPreview &&
                enhancedContent && (
                    <PublishStatusBadge
                        hasUnpublishedChanges={
                            enhancedContent?.hasUnpublishedChanges() ?? true
                        }
                        small={true}
                        status={
                            enhancedContent?.getPublishStatus() ??
                            PublishStatus.Unpublished
                        }
                    />
                )}
        </React.Fragment>
    );
    return (
        <div
            className={`${BASE_CLASS_NAME} ${expandedCSSModifier}`}
            id={
                enhancedContentManifest?.getHtmlId() ??
                enhancedContent?.getHtmlId() ??
                undefined
            }
            ref={observerRef?.(enhancedContentManifest?.id!)}>
            <div
                className={`${BASE_CLASS_NAME}__header`}
                onClick={handleToggle}>
                {enhancedContentHeaderTitleAndPublishInfo}
                {isLoading && (!isMobile || !isModalOpen) && (
                    <Loader type={LoaderStyles.LinkGlyphBlue} />
                )}
                {!isPreview && !isPrintView && (
                    <Button
                        accessibleText={collapsedStatusText}
                        ariaControls={accordionContentId}
                        ariaExpanded={!isCollapsed}
                        cssClassName={`${BASE_CLASS_NAME}__header__toggle`}
                        onClick={handleToggleButtonClick}
                        style={ButtonStyles.None}>
                        {collapsedStatusText}
                        <Icon type={iconType} />
                    </Button>
                )}
            </div>
            {isMobile ? (
                <Modal
                    cssClassName={`${BASE_CLASS_NAME} -mobile-modal`}
                    closeDialog={handleToggle}
                    isVisible={isModalOpen}
                    label={t("enhancedContent")}
                    type={ModalTypes.Base}
                    transition={ModalTransitions.SlideUp}>
                    <div className={`${BASE_CLASS_NAME}__header`}>
                        {enhancedContentHeaderTitleAndPublishInfo}
                        {isLoading && (
                            <Loader type={LoaderStyles.LinkGlyphBlue} />
                        )}
                        <ModalCloseButton onClick={handleToggleButtonClick} />
                    </div>
                    {enhancedContent && (
                        <EnhancedContentDetail
                            {...enhancedContentDetailProps}
                        />
                    )}{" "}
                </Modal>
            ) : (
                <React.Fragment>
                    {enhancedContent && (
                        <EnhancedContentDetail
                            {...enhancedContentDetailProps}
                        />
                    )}
                </React.Fragment>
            )}
        </div>
    );
};

// #endregion Component

// -----------------------------------------------------------------------------------------
// #region Export
// -----------------------------------------------------------------------------------------

export default EnhancedContentPanel;

// #endregion Export
